import { createBrowserRouter, Navigate } from 'react-router-dom';
import { BKASH_ENABLED, PAY_FALLBACK_PATH } from '../../config/features';
import { TierLevel } from '../../domain/enums/TierLevel';
import Shell from '../layouts/Shell';
import LoginPage from '../pages/LoginPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import DashboardPage from '../pages/DashboardPage';
import InstallmentsPage from '../pages/InstallmentsPage';
import PaymentsHistoryPage from '../pages/PaymentsHistoryPage';
import BankSlipUploadPage from '../pages/BankSlipUploadPage';
import InvoicePage from '../pages/InvoicePage';
import CommissionsPage from '../pages/CommissionsPage';
import WithdrawalsPage from '../pages/WithdrawalsPage';
import TeamPage from '../pages/team/TeamPage';
import NoticesPage from '../pages/NoticesPage';
import ProfilePage from '../pages/ProfilePage';
import BkashCheckoutPage from '../pages/BkashCheckoutPage';
import PendingContractDecision from '../pages/PendingContractDecision';
import NotFound from '../pages/NotFound';
import { RouteErrorBoundary } from '../components/feedback/RouteErrorBoundary';
import { RequireSubscriber, RequireTier } from './guards';

/**
 * Route map (frozen plan §4.1 + knowledge-tree §6):
 * - Shell wraps every authenticated route (RequireAuth inside).
 * - Full interactive journeys for bKash Checkout (W08).
 * - Role gates: /withdrawals L2+, /team L2+.
 */
export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorBoundary /> },
  { path: '/forgot-password', element: <ForgotPasswordPage />, errorElement: <RouteErrorBoundary /> },
  {
    path: '/',
    element: <Shell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <DashboardPage /> },
      // Installments and payments belong to subscribers (L1); executives (L2-L6) are redirected to the dashboard.
      { path: 'installments', element: <RequireSubscriber><InstallmentsPage /></RequireSubscriber> },
      { path: 'payments', element: <RequireSubscriber><PaymentsHistoryPage /></RequireSubscriber> },
      { path: 'payments/bank-slip/upload', element: <RequireSubscriber><BankSlipUploadPage /></RequireSubscriber> },
      {
        path: 'payments/bkash',
        // E11/E12 are not built on the backend yet; deep links fall back to bank-slip upload.
        element: (
          <RequireSubscriber>
            {BKASH_ENABLED ? <BkashCheckoutPage /> : <Navigate to={PAY_FALLBACK_PATH} replace />}
          </RequireSubscriber>
        ),
      },
      { path: 'payments/:id/invoice', element: <RequireSubscriber><InvoicePage /></RequireSubscriber> },
      {
        path: 'commissions',
        // W07 invariant #1: commission interfaces need tier L2 or above —
        // the nav link is hidden AND the route itself is gated (Phase-3 fix).
        element: (
          <RequireTier minTier={TierLevel.L2}>
            <CommissionsPage />
          </RequireTier>
        ),
      },
      {
        path: 'withdrawals',
        element: (
          <RequireTier minTier={TierLevel.L2}>
            <WithdrawalsPage />
          </RequireTier>
        ),
      },
      {
        path: 'team',
        element: (
          <RequireTier minTier={TierLevel.L2}>
            <TeamPage />
          </RequireTier>
        ),
      },
      { path: 'notices', element: <NoticesPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'profile/kyc/edit', element: <PendingContractDecision /> }, // HELD — E05
      { path: '*', element: <NotFound /> },
    ],
  },
]);
